import React, { useState } from 'react';
import { Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useTranslation } from 'react-i18next';
import AddServicePointsModal from './AddServicePointsModal';
import ImportFeedback from './ImportFeedback';
import ImportReportBanner from './ImportReportBanner';
import DeletionReportBanner from './DeletionReportBanner';
import InterventionTypeReportBanner from './InterventionTypeReportBanner';
import SendToAssignmentReportBanner from './SendToAssignmentReportBanner';
import RequestsTable from './RequestsTable';
import { usePdsImport } from './usePdsImport';
import { useRequestsToQualify } from './useRequestsToQualify';
import TableFilters, { FilterSelect } from '../../../../../Tools/Table/TableFilters';
import TableSearch from '../../../../../Tools/Table/TableSearch';
import {
  FilterValue,
  NO_FILTERS,
  TableFilters as Filters,
  clearFilters,
  withSearch
} from '../../../../../Tools/Table/filters';
import {
  QUALIFY_FILTER_ENDPOINTS,
  useFilterFields,
  useStateFilterField
} from '../../useFilterFields';
import useDebouncedValue from '../../../../../../hooks/useDebouncedValue';

export default function RequestsPanel() {
  const { t } = useTranslation();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [search, setSearch] = useState('');

  // La frappe reste immédiate à l'écran ; seule la requête attend la fin de la saisie.
  const debouncedSearch = useDebouncedValue(search, 300);

  const filterFields = useFilterFields(QUALIFY_FILTER_ENDPOINTS, filters, {
    withDiameter: true
  });
  const stateField = useStateFilterField('interventionCreation.filters.toQualify');

  const changeFilter = (name: string, value: FilterValue) =>
    setFilters((previous) => ({ ...previous, [name]: value }));

  // Le bouton ne vide que le bloc dépliable : l'état et la recherche, posés ailleurs
  // dans la barre d'outils, lui survivent.
  const resetFilters = () =>
    setFilters((previous) =>
      clearFilters(
        previous,
        filterFields.map((field) => field.name)
      )
    );

  // L'import vit ici et non dans la modale : celle-ci se ferme dès qu'il est terminé, et
  // react-query n'appelle les callbacks de `mutate` que tant que le composant est monté.
  const importState = usePdsImport();

  const { rows, isFetching } = useRequestsToQualify(withSearch(filters, debouncedSearch));

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography variant="h6">{t('interventionCreation.requests.title')}</Typography>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setIsAddModalOpen(true)}
          >
            {t('interventionCreation.addServicePoints.title')}
          </Button>
        </Box>

        <ImportFeedback importState={importState} />

        <ImportReportBanner />

        <DeletionReportBanner />

        <InterventionTypeReportBanner />

        <SendToAssignmentReportBanner />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* La recherche prend la largeur restante, comme sur la maquette. */}
          <Box sx={{ flexGrow: 1, minWidth: 240 }}>
            <TableSearch value={search} onChange={setSearch} />
          </Box>
          <FilterSelect field={stateField} values={filters} onChange={changeFilter} />
          <Button
            variant="outlined"
            startIcon={<TuneIcon />}
            onClick={() => setAreFiltersOpen((open) => !open)}
          >
            {t('interventionCreation.filters.toggle')}
          </Button>
        </Box>

        {areFiltersOpen && (
          <TableFilters
            fields={filterFields}
            values={filters}
            onChange={changeFilter}
            onReset={resetFilters}
          />
        )}

        {isFetching && <LinearProgress />}

        <RequestsTable rows={rows} isLoading={isFetching} />
      </Stack>
      <AddServicePointsModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        importState={importState}
      />
    </Paper>
  );
}
